import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const now = new Date();

export interface SubscriptionAlertItem {
  nursery_id: string;
  nursery_name: string;
  type: "overdue" | "missing";
  due_date?: string | null;
  amount?: number | null;
  invoice_id?: string | null;
}

export interface SubscriptionAlertsData {
  month: number;
  year: number;
  overdue: SubscriptionAlertItem[];
  missing: SubscriptionAlertItem[];
  total: number;
}

/**
 * Detects overdue / missing subscription invoices for the current month.
 * Used to surface alerts to accountant & admin users.
 */
export const useSubscriptionAlerts = () => {
  const month = now.getMonth() + 1;
  const year = now.getFullYear();

  return useQuery<SubscriptionAlertsData>({
    queryKey: ["subscription-alerts", year, month],
    refetchInterval: 5 * 60 * 1000,
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const [{ data: nurseries }, { data: invoices }] = await Promise.all([
        supabase.from("nurseries").select("id, name"),
        supabase
          .from("nursery_subscription_invoices" as any)
          .select("id, nursery_id, due_date, status, amount")
          .eq("year", year)
          .eq("month", month),
      ]);

      const list = (nurseries || []) as { id: string; name: string }[];
      const invByNursery = new Map<string, any>();
      ((invoices || []) as any[]).forEach((i) => invByNursery.set(i.nursery_id, i));

      const overdue: SubscriptionAlertItem[] = [];
      const missing: SubscriptionAlertItem[] = [];

      for (const n of list) {
        const inv = invByNursery.get(n.id);
        if (!inv) {
          missing.push({ nursery_id: n.id, nursery_name: n.name, type: "missing" });
          continue;
        }
        if (inv.status !== "paid" && inv.due_date && new Date(inv.due_date) < today) {
          overdue.push({
            nursery_id: n.id,
            nursery_name: n.name,
            type: "overdue",
            due_date: inv.due_date,
            amount: Number(inv.amount || 0),
            invoice_id: inv.id,
          });
        }
      }

      return { month, year, overdue, missing, total: overdue.length + missing.length };
    },
  });
};
